/**
 * useCioRecs — shared CIO recommendations fetch flow (SFCC-11581).
 *
 * Encapsulates the two-step client flow every recs surface repeats:
 *   1. CIO client SDK `getRecommendations(podId)` → product IDs (+ strategy IDs,
 *      result_id, pod display_name).
 *   2. SCAPI hydration via `/resources/product-recommendations` → full product
 *      data (swatches + pricing), mapped to `HecProduct`.
 *
 * Extracted from `ProductRecommendations` so the Embla recs component and the
 * cart recs surface share one implementation instead of re-deriving it inline
 * (the drift risk called out in SFCC-11581). `strategyById` + `resultId` are
 * captured here for the `recommendation_click` beacon; the canonical component
 * ignores them, cart uses them.
 *
 * Architecture: CIO Path A §2 (client-SDK-only recs). See docs/cio-path-a-architecture.md.
 */
import { useEffect, useState } from "react";

import type { ProductHitWithSwatches } from "~/shop/products/productSearch.server";

import { useAuth } from "~/hec/context/auth-context";
import type { HecProduct } from "~/hec/types/product";
import { productHitToHecProduct } from "~/hec/utils/product-adapters";
import { useFailSoftResourceState } from "~/routes/resources/hooks/use-failsoft-resource";
import type { loader as recsLoader } from "~/routes/resources/product-recommendations";
import { getProductHitImage } from "~/shop/utils/image";
import { fetchCioRecommendations } from "~/utils/cio-recommendations.client";
import { useCioClient } from "~/utils/constructor";

import { CIO_RECS_BEACON_SCOPE } from "./recs-beacon-scopes";

export interface UseCioRecsOptions {
  /** Seed product IDs for "similar items" pods (e.g. cart line items, PDP master). */
  itemIds?: string[];
  /** Filter by group_id (e.g. current category). */
  groupId?: string;
  numResults?: number;
  /** When false, skip the CIO fetch entirely (e.g. per-site recs disabled). */
  enabled?: boolean;
}

export interface UseCioRecsResult {
  /** Hydrated products (image-normalized). Empty until SCAPI resolves. */
  products: HecProduct[];
  /** CIO-resolved product IDs; `null` until the CIO call settles. */
  productIds: string[] | null;
  /** CIO pod display_name, if any (consumer applies its own fallback). */
  heading: string | undefined;
  /** CIO result_id for `recommendation_click` beacon attribution. */
  resultId: string | undefined;
  /** productId → CIO strategy.id, for the click beacon. */
  strategyById: Map<string, string>;
  /**
   * CIO in-flight OR SCAPI hydration in-flight. A failed hydrate is NOT
   * in-flight (COM-63): it settles, `isLoading` drops, and `returnedNothing`
   * takes over — never a permanent skeleton.
   */
  isLoading: boolean;
  /**
   * The most recent CIO call failed. Independent of `products`: on a failed
   * RE-fetch (auth flip) the previously resolved ids/products are retained, so
   * `error` can be true while `products` is non-empty. Consumers that
   * `if (error) return null` hide; those that ignore it keep showing the
   * retained carousel.
   */
  error: boolean;
  /**
   * Settled with zero results and nothing retained: CIO returned no ids (or
   * failed COLD), or the SCAPI hydrate returned none / failed. Never true while
   * `products` is non-empty.
   */
  returnedNothing: boolean;
}

const EMPTY_STRATEGY = new Map<string, string>();

export function useCioRecs(
  podId: string,
  { itemIds, groupId, numResults = 10, enabled = true }: UseCioRecsOptions = {},
): UseCioRecsResult {
  const [productIds, setProductIds] = useState<string[] | null>(null);
  const [heading, setHeading] = useState<string | undefined>(undefined);
  const [resultId, setResultId] = useState<string | undefined>(undefined);
  const [strategyById, setStrategyById] =
    useState<Map<string, string>>(EMPTY_STRATEGY);
  const [error, setError] = useState(false);

  const cio = useCioClient();
  // Auth identity — refetch when the shopper signs in/out (SFCC-11869: the
  // account-drawer sign-in revalidates the layout loader in place, flipping
  // AuthProvider's isLoggedIn) so recs re-resolve against the new
  // (personalized vs guest) CIO identity instead of staying stale.
  const { isLoggedIn } = useAuth();

  // Step 1 — CIO client SDK call on mount / pod change.
  useEffect(() => {
    if (!cio || !enabled) return;
    let cancelled = false;
    // Each run starts clean, so a transient failure is not permanent: without
    // this the one-way `error` flag let a post-sign-in flake collapse a
    // carousel that had rendered fine for the guest (consumers do
    // `if (error) return null`) with no way back short of a document load.
    // The `isLoggedIn` dep costs one extra CIO request + impression per pod per
    // auth flip; that is the price of re-resolving the identity.
    setError(false);
    fetchCioRecommendations(cio, podId, { numResults, itemIds, groupId }).then(
      res => {
        if (cancelled) return;
        if (!res) {
          // COM-63 (sweep EC-01) — a COLD failure settles to an empty list,
          // not `error` alone: `isLoading` is `productIds === null || …`, so
          // leaving ids null kept consumers that skeleton on `isLoading`
          // skeletoning forever. Cold ONLY (`prev ?? []`): this effect also
          // re-runs on an auth flip, and a failed re-fetch must keep the ids
          // (and products) already showing — consumers that ignore `error`
          // (cart-bottom-recs) would otherwise lose a rendered carousel, and
          // `returnedNothing` would go true with products still present.
          // `error` is set either way so consumers can tell failed from empty.
          setProductIds(prev => prev ?? []);
          setError(true);
          return;
        }
        const ids: string[] = [];
        const strategies = new Map<string, string>();
        for (const item of res.response.results ?? []) {
          const pid = (item.data?.["id"] as string | undefined) ?? "";
          if (!pid) continue;
          ids.push(pid);
          strategies.set(pid, (item.strategy?.id as string | undefined) ?? "");
        }
        setResultId(res.result_id);
        setStrategyById(strategies.size ? strategies : EMPTY_STRATEGY);
        setHeading(res.response.pod?.display_name ?? undefined);
        setProductIds(ids);
      },
    );
    return () => {
      cancelled = true;
    };
    // itemIds is a fresh array each render; only refetch when the pod changes
    // or auth flips (guest <-> registered = different CIO identity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podId, cio, enabled, isLoggedIn]);

  // Step 2 — SCAPI hydration once we have IDs. Owns its own fetch rather than a
  // router fetcher: recs are non-critical background hydration, and a
  // `fetcher.load()` rejection is THROWN to the owning route's boundary (the
  // cart/layout routes export none, so it reaches RootErrorBoundary and blanks
  // the storefront). The rejection that matters here is the abort the browser
  // issues when the shopper navigates away mid-fetch — a cancellation, not a
  // fault, and it must leave the page exactly as it was (SFCC-11869).
  const recsHref =
    productIds && productIds.length > 0
      ? `/resources/product-recommendations?${new URLSearchParams({
          sysId: podId,
          productIDs: productIds.join(","),
        })}`
      : null;
  const {
    data: recsData,
    isLoading: recsLoading,
    failed: recsFailed,
  } = useFailSoftResourceState<Awaited<ReturnType<typeof recsLoader>>>(
    recsHref,
    undefined,
    CIO_RECS_BEACON_SCOPE,
  );

  const products: HecProduct[] = (
    (recsData?.recommendations ?? []) as ProductHitWithSwatches[]
  ).map(p => {
    const hec = productHitToHecProduct(p);
    if (!hec.image_url) hec.image_url = getProductHitImage(p)?.link;
    return hec;
  });

  // COM-63 (sweep EC-01) — `recsFailed` makes a COLD hydrate failure terminal:
  // without it `!recsData` stays true after a 5xx / WAF 403 / network failure,
  // `isScapiLoading` never drops, and the carousel skeletons forever. A failed
  // RE-fetch keeps the last good value (`failed` is false then by design).
  const isScapiLoading =
    recsLoading ||
    (productIds !== null && productIds.length > 0 && !recsData && !recsFailed);
  const isLoading = enabled && (productIds === null || isScapiLoading);

  const cioReturnedNothing = productIds !== null && productIds.length === 0;
  // Settled with nothing to show — the route returned zero products or the
  // hydrate failed outright. Either way the section hides.
  const scapiReturnedNothing =
    (!!recsData || recsFailed) && products.length === 0;
  const returnedNothing = cioReturnedNothing || scapiReturnedNothing;

  return {
    products,
    productIds,
    heading,
    resultId,
    strategyById,
    isLoading,
    error,
    returnedNothing,
  };
}
